import { execute } from '@/graphql/execute';
import { deletionOperationsQuery } from '@/queries/bulkDeletion.query';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const statusKey = {
  PENDING: 'bulkDelete.statusPending',
  RUNNING: 'bulkDelete.statusRunning',
  COMPLETED: 'bulkDelete.statusCompleted',
  RETRYING: 'bulkDelete.statusRetrying',
  FAILED: 'bulkDelete.statusFailed',
} as const;

const ACTIVE = ['PENDING', 'RUNNING', 'RETRYING'];

/** A finished cleanup stays listed this long, so whoever ran it sees it land
 *  (the delete dialog shows no toast). The server keeps completed ones for a
 *  week and for every editor of the project, which made a days-old
 *  "Delete batches: Completed (4/4)" sit under the lists of people who never
 *  deleted anything. */
const RECENT_COMPLETED_MS = 15 * 60 * 1000;

export function BulkDeletionStatus({ projectId }: { projectId: string }) {
  const { t } = useTranslation('project');
  const { data, dataUpdatedAt } = useQuery({
    queryKey: ['deletionOperations', projectId],
    queryFn: () => execute(deletionOperationsQuery, { projectId }),
    refetchInterval: (query) =>
      query.state.data?.deletionOperations.some((operation) => ACTIVE.includes(operation.status)) ? 5000 : false,
  });
  const operations = (data?.deletionOperations ?? []).filter(
    (operation) =>
      operation.status !== 'COMPLETED' ||
      (operation.completedAt != null &&
        dataUpdatedAt - new Date(operation.completedAt).getTime() < RECENT_COMPLETED_MS),
  );
  if (!operations.length) return null;
  return (
    <section
      aria-label={t('bulkDelete.operationStatus')}
      className='mt-3 rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-stone-700'>
      <h3 className='mb-1 text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-stone-400'>
        {t('bulkDelete.operationStatus')}
      </h3>
      <ul className='space-y-1'>
        {operations.map((operation) => {
          const failed = operation.status === 'FAILED';
          const Icon = failed ? AlertTriangle : operation.status === 'COMPLETED' ? CheckCircle2 : Loader2;
          return (
            <li key={operation.id} role='status' className='flex min-w-0 items-start gap-2'>
              <Icon
                aria-hidden
                className={`mt-0.5 size-4 shrink-0 ${
                  failed
                    ? 'text-destructive'
                    : operation.status === 'COMPLETED'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'animate-spin text-slate-500 dark:text-stone-400'
                }`}
              />
              <div className='min-w-0 flex-1'>
                <div className='flex min-w-0 items-baseline gap-2'>
                  <span className='truncate'>
                    {t(operation.kind === 'LAYERS' ? 'bulkDelete.kindLayers' : 'bulkDelete.kindBatches')}
                  </span>
                  <span className={`ml-auto shrink-0 text-xs ${failed ? 'text-destructive' : 'text-slate-500 dark:text-stone-400'}`}>
                    {t(statusKey[operation.status])}
                    {operation.totalTargets > 0 &&
                      ` · ${t('bulkDelete.filesProgress', {
                        done: operation.completedTargets,
                        total: operation.totalTargets,
                      })}`}
                  </span>
                </div>
                {operation.lastError && (
                  <p className='text-destructive truncate text-xs' title={operation.lastError}>
                    {operation.lastError}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
